// src/services/workshop3Service.js
import api from '../api/apiClient';

/**
 * Workshop 3 Service - Uses hydrated data from backend
 * This service fetches strategic scenarios and attack paths that are computed on-the-fly
 * from base data (mappings, sources, events) instead of stored redundant copies
 */
const workshop3Service = {
    /**
     * Get hydrated strategic scenarios for an analysis
     * @param {string} analysisId - Analysis ID
     * @returns {Promise<Object>} - Response with hydrated scenarios
     */
    getStrategicScenarios: async (analysisId) => {
        try {
            const response = await api.get(`/analyses/${analysisId}/workshop3/strategic-scenarios`);
            return response;
        } catch (error) {
            console.error('Error fetching strategic scenarios:', error);
            throw error;
        }
    },

    /**
     * Get hydrated attack paths for an analysis
     * @param {string} analysisId - Analysis ID
     * @param {boolean} [retainedOnly=false] - If true, only return retained attack paths (like W4)
     * @returns {Promise<Object>} - Response with hydrated attack paths
     */
    getAttackPaths: async (analysisId, retainedOnly = false) => {
        try {
            const params = retainedOnly ? { retainedOnly: 'true' } : {};
            const response = await api.get(`/analyses/${analysisId}/workshop3/attack-paths`, { params });
            return response;
        } catch (error) {
            console.error('Error fetching attack paths:', error);
            throw error;
        }
    },

    /**
     * Get operational scenarios for an analysis
     * @param {string} analysisId - Analysis ID
     * @returns {Promise<Object>} - Response with operational scenarios
     */
    getOperationalScenarios: async (analysisId) => {
        try {
            const response = await api.get(`/analyses/${analysisId}/cti-operational-scenarios`);
            return response;
        } catch (error) {
            console.error('Error fetching operational scenarios:', error);
            throw error;
        }
    },

    // REMOVED: getScenariosAndPaths — never called by any component, individual endpoints used instead

    /**
     * Get synchronized ecosystem measures (auto-cleans orphaned measures)
     * @param {string} analysisId - Analysis ID
     * @param {boolean} autoClean - If true, automatically removes orphaned measures from DB
     * @returns {Promise<Object>} - Response with synced measures and metadata
     */
    getSyncedEcosystemMeasures: async (analysisId, autoClean = false) => {
        try {
            const response = await api.get(
                `/analyses/${analysisId}/workshop3/ecosystem-measures-synced`,
                { params: { autoClean: autoClean.toString() } }
            );
            return response;
        } catch (error) {
            console.error('Error fetching synced ecosystem measures:', error);
            throw error;
        }
    },

    /**
     * Get all threat intelligence data in one bulk call
     * Combines: attack paths + business assets + CTI results
     * @param {string} analysisId - Analysis ID
     * @returns {Promise<Object>} - Response with { attackPaths, businessAssets, ctiResults, ctiStatus, timestamps }
     */
    getThreatIntelligenceData: async (analysisId) => {
        try {
            console.log('🔄 Loading all threat intelligence data in bulk...');
            const response = await api.get(`/analyses/${analysisId}/workshop3/threat-intelligence-data`);
            if (response.success) {
                console.log('✅ Bulk load complete:', {
                    attackPaths: response.data.attackPaths?.length || 0,
                    businessAssets: Object.keys(response.data.businessAssets || {}).length,
                    ctiResults: Object.keys(response.data.ctiResults || {}).length
                });
            }
            return response;
        } catch (error) {
            console.error('Error fetching threat intelligence data:', error);
            throw error;
        }
    },

    /**
     * Get all data for Operational Scenarios AI page in one bulk call
     * Combines: attack paths + CTI data + business values + dreaded events
     * @param {string} analysisId - Analysis ID
     * @returns {Promise<Object>} - Response with { attackPaths, attackPathsWithCTI, businessValues, dreadedEvents, summary }
     */
    getOperationalScenariosData: async (analysisId) => {
        try {
            console.log('🔄 Loading all operational scenarios data in bulk...');
            const response = await api.get(`/analyses/${analysisId}/workshop3/operational-scenarios-data`);
            if (response.success) {
                console.log('✅ Bulk load complete:', {
                    attackPaths: response.data.attackPaths?.length || 0,
                    attackPathsWithCTI: response.data.attackPathsWithCTI?.length || 0,
                    businessValues: response.data.businessValues?.length || 0,
                    dreadedEvents: response.data.dreadedEvents?.length || 0
                });
            }
            return response;
        } catch (error) {
            console.error('Error fetching operational scenarios data:', error);
            throw error;
        }
    },

    /**
     * Toggle attack path retention for subsequent workshops
     * @param {string} analysisId - Analysis ID
     * @param {string} pathId - Attack path ID
     * @param {boolean} retained - Whether to retain or not
     * @returns {Promise<Object>} - Response
     */
    toggleAttackPathRetention: async (analysisId, pathId, retained) => {
        try {
            const response = await api.put(`/analyses/${analysisId}/attack-paths/${pathId}/retain`, { retained });
            return response;
        } catch (error) {
            console.error('Error toggling attack path retention:', error);
            throw error;
        }
    },

    /**
     * Update editable fields on an attack path (e.g. description)
     * @param {string} analysisId - Analysis ID
     * @param {string} pathId - Attack path ID
     * @param {Object} data - Partial path fields to merge (e.g. { description })
     * @returns {Promise<Object>} - Response with updated path
     */
    updateAttackPath: async (analysisId, pathId, data) => {
        try {
            const response = await api.put(`/analyses/${analysisId}/attack-paths/${pathId}`, data);
            return response;
        } catch (error) {
            console.error('Error updating attack path:', error);
            throw error;
        }
    },

    /**
     * Set preliminary mode on an attack path (no operational scenario, manual likelihood)
     * @param {string} analysisId - Analysis ID
     * @param {string} pathId - Attack path ID
     * @param {Object} payload - { likelihood: 1-4, justification?: string }
     * @returns {Promise<Object>} - Response
     */
    setPreliminaryLikelihood: async (analysisId, pathId, payload) => {
        try {
            const response = await api.put(`/analyses/${analysisId}/attack-paths/${pathId}/preliminary`, payload);
            return response;
        } catch (error) {
            console.error('Error setting attack path preliminary likelihood:', error);
            throw error;
        }
    },

    /**
     * Clear preliminary mode on an attack path
     * @param {string} analysisId - Analysis ID
     * @param {string} pathId - Attack path ID
     * @returns {Promise<Object>} - Response
     */
    clearPreliminaryLikelihood: async (analysisId, pathId) => {
        try {
            const response = await api.put(`/analyses/${analysisId}/attack-paths/${pathId}/preliminary`, { clear: true });
            return response;
        } catch (error) {
            console.error('Error clearing attack path preliminary mode:', error);
            throw error;
        }
    }
};

export default workshop3Service;
